import React, { useState } from "react";
import "../styles.css";
import { useNavigate } from "react-router-dom";
import { auth, provider } from "../firebase";
import { 
  signInWithPopup, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut,
  sendEmailVerification,
  updateProfile,
  onAuthStateChanged
} from "firebase/auth";
import { normalizeRole } from "../utils/roleUtils";
import { sendVerificationEmail, sendWelcomeEmail, sendPasswordResetEmail } from "../utils/emailService";
import { doc, setDoc, serverTimestamp, getDoc, runTransaction } from "firebase/firestore";
import { db } from "../firebase";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import GoogleLogo from "../assets/google-logo.svg";
import TermsModal from "../components/TermsModal";
import TextCaptcha from "../components/TextCaptcha";

// Role Selection Modal Component
const RoleSelectionModal = ({ isOpen, onClose, onSelect }) => {
  if (!isOpen) return null;

  const roles = [
    { 
      key: 'parent', 
      label: 'Parent/Caregiver', 
      description: 'Find support for your child',
      icon: '👨‍👩‍👧‍👦' 
    },
    { 
      key: 'professional', 
      label: 'Professional', 
      description: 'Therapists, doctors, and specialists',
      icon: '👩‍⚕️' 
    },
    { 
      key: 'organization', 
      label: 'Organization', 
      description: 'Clinics, schools, and centers',
      icon: '🏢' 
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md">
        <form className="mt-6 space-y-4">
          <div className="rounded-md -space-y-4">
            {roles.map((role) => (
              <button
                key={role.key}
                onClick={() => onSelect(role.key)}
                className="w-full flex items-start gap-4 p-4 text-left rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-indigo-200 hover:shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <span className="text-2xl" role="img" aria-label={role.label}>{role.icon}</span>
                <div>
                  <div className="font-medium text-gray-900">{role.label}</div>
                  <div className="text-sm text-gray-500">{role.description}</div>
                </div>
              </button>
            ))}
          </div>
        </form>
        <div className="mt-6 text-center">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const Signup = ({ role: initialRole, onSuccess }) => {
  const [showRoleModal, setShowRoleModal] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(initialRole || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [isRegister, setIsRegister] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const navigate = useNavigate();

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setShowRoleModal(false);
    setShowAuthModal(true);
    setIsRegister(false); // Show login form by default
  };

  const handleGoogleSignIn = async () => {
    if (!selectedRole) {
      setShowRoleModal(true);
      setShowAuthModal(false);
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Check if this is a new user
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const isNewUser = !userDoc.exists();
      
      if (isNewUser) {
        // New user - create user document
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          role: selectedRole,
          emailVerified: user.emailVerified,
          status: 'active',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        // If user is an organization, create an organization profile
        if (selectedRole === 'organization') {
          await setDoc(doc(db, 'organizations', user.uid), {
            uid: user.uid,
            orgName: user.displayName || '',
            email: user.email,
            contactEmail: user.email,
            status: 'pending',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            // Add other default organization fields as needed
            orgType: '',
            description: '',
            website: '',
            contactPhone: '',
            city: '',
            state: '',
            country: 'India',
            logoUrl: user.photoURL || '',
            bannerUrl: '',
            socialMedia: {
              facebook: '',
              twitter: '',
              linkedin: '',
              instagram: '',
              youtube: ''
            },
            isPublic: false,
            searchKeywords: []
          });
        }
      }
      
      // Send welcome and verification emails for new users
      if (isNewUser && user.email) {
        try {
          // Send welcome email
          await sendWelcomeEmail(user, user.displayName || "User");
          
          // Send verification email if not verified
          if (!user.emailVerified) {
            await sendVerificationEmail(user, user.displayName || "User");
          }
        } catch (emailError) {
          console.error('Error sending welcome/verification email:', emailError);
          // Don't fail the sign-in process if email fails
        }
      }
      
      // If email is not verified, sign out and show error
      if (!user.emailVerified) {
        await auth.signOut();
        throw new Error('Please verify your Google account email before signing in.');
      }
      
      // Navigate to dashboard or call onSuccess
      if (onSuccess) {
        onSuccess(selectedRole);
      } else {
        navigate('/app/profile', { replace: true });
      }
      
    } catch (error) {
      console.error("Google sign in error:", error);
      const errorMessage = error.code === 'auth/account-exists-with-different-credential' 
        ? 'An account already exists with this email. Please sign in with your existing method.'
        : error.message || 'Failed to sign in with Google';
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    if (!resetEmail) {
      setError('Please enter your email address to reset your password');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Use our custom password reset email
      await sendPasswordResetEmail(resetEmail);
      setResetSent(true);
      toast.success('Password reset email sent. Please check your inbox.');
      
      // Hide the form after 3 seconds
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetSent(false);
        setResetEmail('');
      }, 3000);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message || 'Failed to send password reset email');
      toast.error(error.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    
    // Check if CAPTCHA is verified
    if (!captchaVerified) {
      setError('Please complete the CAPTCHA verification');
      return;
    }
    
    setLoading(true);

    try {
      if (isRegister) {
        // Registration flow
        if (password !== confirmPassword) {
          throw new Error("Passwords do not match");
        }
        
        if (!acceptedTerms) {
          throw new Error("Please accept the terms and conditions");
        }
        
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        // Update user profile with display name
        await updateProfile(user, { displayName: fullName });
        
        // Send verification email using our custom service
        await sendVerificationEmail(user, fullName);
        
        // Send welcome email
        await sendWelcomeEmail(user, fullName);
        
        // Create user document in Firestore
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          email: user.email,
          displayName: fullName,
          photoURL: "",
          role: selectedRole,
          emailVerified: false,
          status: 'inactive',  // New users are inactive by default
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        // Create role-specific profile based on user type
        if (selectedRole === 'organization') {
          await setDoc(doc(db, 'organizations', user.uid), {
            uid: user.uid,
            orgName: fullName,
            email: user.email,
            contactEmail: user.email,
            status: 'pending',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            orgType: '',
            description: '',
            website: '',
            contactPhone: '',
            city: '',
            state: '',
            country: 'India',
            logoUrl: '',
            bannerUrl: '',
            socialMedia: {
              facebook: '',
              twitter: '',
              linkedin: '',
              instagram: '',
              youtube: ''
            },
            isPublic: false,
            searchKeywords: []
          });
        } else if (selectedRole === 'professional') {
          await setDoc(doc(db, 'professionals', user.uid), {
            uid: user.uid,
            displayName: fullName,
            email: user.email,
            status: 'pending',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            // Add professional-specific fields
            title: '',
            specialization: [],
            bio: '',
            experience: '',
            education: [],
            certifications: [],
            languages: [],
            location: {
              city: '',
              state: '',
              country: 'India'
            },
            contact: {
              phone: '',
              email: user.email,
              website: ''
            },
            socialMedia: {
              linkedin: '',
              twitter: '',
              facebook: ''
            },
            isAvailable: false,
            searchKeywords: []
          });
        }
        
        // Show success message and switch to login view
        toast.success('Account created! Please check your email to verify your account.');
        setIsRegister(false);
      } else {
        // Login flow - check if email is verified
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        if (!user.emailVerified) {
          await auth.signOut();
          throw new Error('Please verify your email before signing in. Check your inbox (and spam folder) for the verification link.');
        }
        
        // Redirect based on role
        if (onSuccess) {
          onSuccess(selectedRole);
        } else {
          navigate('/app/profile', { replace: true });
        }
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError(error.message || 'Authentication failed. Please try again.');
      toast.error(error.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };
  // Render the authentication modal content
  const renderAuthModal = () => (
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
      <div className="p-6 pb-4 relative">
        {/* Close Button */}
        <button
          onClick={() => setShowAuthModal(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 focus:outline-none"
          aria-label="Close modal"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <h2 className="text-2xl font-bold text-center mb-4">
          {isRegister ? 'Create Account' : 'Sign In'} as {selectedRole}
        </h2>
      </div>
      
      <div className="px-6 pb-6 overflow-y-auto flex-1">
        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Google Sign In */}
        <div className="space-y-4">
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <img src={GoogleLogo} alt="Google logo" className="h-5 w-5" />
            <span>Continue with Google</span>
          </button>

          <div className="mt-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500 text-xs">Or continue with email</span>
              </div>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleEmailAuth} className="space-y-4">
            {isRegister && (
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  required
                  disabled={loading}
                />
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                required
                disabled={loading}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                {!isRegister && (
                  <button
                    type="button"
                    onClick={() => {
                      setResetEmail(email);
                      setShowForgotPassword(!showForgotPassword);
                    }}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-500 hover:underline whitespace-nowrap ml-2"
                  >
                    {showForgotPassword ? 'Hide' : 'Forgot password?'}
                  </button>
                )}
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                required
                minLength={6}
                disabled={loading}
              />
              
              {/* Inline Forgot Password Form */}
              {showForgotPassword && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Reset your password</h4>
                  <form onSubmit={handleForgotPassword} className="flex gap-2">
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Enter your email"
                      required
                      disabled={loading}
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {loading ? 'Sending...' : 'Send Link'}
                    </button>
                  </form>
                  {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
                  {resetSent && (
                    <div className="mt-2 p-2 bg-green-50 text-green-700 text-sm rounded">
                      Check your email for the reset link
                    </div>
                  )}
                </div>
              )}
            </div>

            {isRegister && (
              <>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    required
                    minLength={6}
                    disabled={loading}
                  />
                </div>

                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="terms"
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-1 focus:ring-indigo-500 border-gray-300 rounded"
                      required
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="terms" className="text-gray-700">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowTerms(true);
                        }}
                        className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:ring-offset-1 rounded px-1 -mx-1"
                      >
                        Terms & Conditions
                      </button>
                    </label>
                  </div>
                </div>
              </>
            )}

            {/* CAPTCHA */}
            <div className="mt-4">
              <TextCaptcha onVerify={setCaptchaVerified} />
            </div>

            <div className="mt-4">
              <button
                type="submit"
                disabled={loading || !captchaVerified}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : isRegister ? 'Sign Up' : 'Sign In'}
              </button>
            </div>
          </form>

          <div className="mt-4 text-center text-sm">
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              className="font-medium text-indigo-600 hover:text-indigo-500 text-sm"
            >
              {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => {
                setShowAuthModal(false);
                setShowRoleModal(true);
              }}
              className="w-full text-center text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to role selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Fallback view (shown when no role is selected)
  const renderWelcomeView = () => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-50 p-4">
      <div className="text-center max-w-md mx-auto">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-indigo-700 mb-2">asdflo</h1>
          <p className="text-gray-600 mb-6">Connecting families with the right support for their unique needs</p>
          <div className="space-y-4">
            <button
              onClick={() => setShowRoleModal(true)}
              className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
            >
              Get Started
            </button>
            <p className="text-sm text-gray-500 mt-4">
              Join our community of parents, professionals, and organizations
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Main render method
  return (
    <div className="relative min-h-screen bg-gray-50">
      {/* Show welcome view when no role is selected and not showing modals */}
      {!selectedRole && !showRoleModal && !showAuthModal && renderWelcomeView()}
      
      {/* Role Selection Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <RoleSelectionModal
            isOpen={showRoleModal}
            onClose={() => setShowRoleModal(false)}
            onSelect={handleRoleSelect}
          />
        </div>
      )}

      {/* Authentication Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          {renderAuthModal()}
        </div>
      )}

      {/* Terms & Conditions Modal */}
      {showTerms && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
              aria-hidden="true"
              onClick={() => setShowTerms(false)}
            ></div>
            
            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-2xl leading-6 font-bold text-gray-900 mb-4">
                      Terms & Conditions
                    </h3>
                    <div className="mt-2 max-h-[60vh] overflow-y-auto pr-2">
                      <div className="space-y-4 text-sm text-gray-700">
                        <h4 className="font-semibold text-lg">1. Acceptance of Terms</h4>
                        <p>
                          By accessing or using the asdflo platform, you agree to be bound by these Terms and Conditions. 
                          If you do not agree to these terms, please do not use our services.
                        </p>
                        
                        <h4 className="font-semibold text-lg">2. User Responsibilities</h4>
                        <p>
                          You are responsible for maintaining the confidentiality of your account information and for all 
                          activities that occur under your account. You agree to notify us immediately of any unauthorized 
                          use of your account.
                        </p>
                        
                        <h4 className="font-semibold text-lg">3. Privacy Policy</h4>
                        <p>
                          Your use of our services is also governed by our Privacy Policy. Please review our Privacy Policy, 
                          which explains how we collect, use, and protect your personal information.
                        </p>
                        
                        <h4 className="font-semibold text-lg">4. Service Modifications</h4>
                        <p>
                          We reserve the right to modify or discontinue, temporarily or permanently, the service with or 
                          without notice at any time. You agree that we shall not be liable to you or any third party for 
                          any modification, suspension, or discontinuance of the service.
                        </p>
                        
                        <h4 className="font-semibold text-lg">5. Limitation of Liability</h4>
                        <p>
                          In no event shall asdflo, nor its directors, employees, partners, agents, suppliers, or affiliates, 
                          be liable for any indirect, incidental, special, consequential, or punitive damages, including 
                          without limitation, loss of profits, data, use, goodwill, or other intangible losses.
                        </p>
                      </div>
                      
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-500 mb-4">
                          Last updated: June 16, 2025
                        </p>
                        <div className="flex justify-end space-x-3">
                          <button
                            type="button"
                            onClick={() => setShowTerms(false)}
                            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            Close
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setAcceptedTerms(true);
                              setShowTerms(false);
                            }}
                            className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            I Accept
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Signup;
